// Milestone 7A (Unified Intelligence Workspace) -- Company Context.
//
// Martian's foundational, brand-controlled understanding of the business.
// Every field here is either genuinely user-provided or genuinely
// inferred from one real, narrow fact (client.industry, at document
// creation only -- see company_context.py's own docstring for the full
// audit trail of what "genuinely available" meant this milestone).
//
// Storing this foundation honestly is the entire scope of 7A. It does
// NOT wire itself into DiagnosticEngine/OpportunityEngine/
// CommerceFindingService/SocialFindingService/PreparedWorkService --
// none of those are touched by this page or its backend. Claiming
// otherwise here would be exactly the kind of fabricated-influence
// claim this product's own discipline forbids.
//
// Competitors are not a field on this page's own document -- they
// reuse the existing, already-working /v2/competitors CRUD
// (competitorsAPI, verified this milestone) rather than becoming a
// second competitor-storage system.

import { useEffect, useRef, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { companyContextAPI, competitorsAPI, competitorSuggestionsAPI } from '@/lib/api';
import { WorkspaceSelect } from '@/components/brand/BrandContextBar';
import { useWorkspaceSelection } from '@/hooks/useWorkspaceSelection';
import { PencilSimple, Check, X, Plus, Trash, Info } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Exported so the discovery/onboarding review screen
// (BrandDiscoveryPage.js) can reuse the exact same field metadata and
// editing components rather than a second Company Context editor --
// same data, same edit affordances, just framed with onboarding-specific
// copy around them there.
// `guidance` is the founder-facing review prompt for each field -- one
// definition shared by onboarding and Your Business (FieldReviewHint).
export const FIELD_META = [
  { key: 'business_summary', label: 'Business & products/services', placeholder: 'What does this business make or do, and for whom?', multiline: true,
    guidance: "Verify the core products or services and who they are for. Martian grounds the work it prepares in this description, so correct anything the business wouldn't stand behind." },
  { key: 'primary_market', label: 'Primary market', placeholder: 'e.g. United Arab Emirates',
    guidance: 'Confirm the market that matters most for growth right now — not simply where the company is registered.' },
  { key: 'audience', label: 'Audience', placeholder: 'Who this business is actually selling to', multiline: true,
    guidance: 'Confirm the people or organisations who actually buy or use the offering, rather than every possible audience.' },
  { key: 'positioning', label: 'Positioning & differentiation', placeholder: 'What makes this business different from the alternatives', multiline: true,
    guidance: "Describe why customers choose this business over alternatives. Remove claims the evidence can't support." },
  { key: 'additional_context', label: 'Additional context', placeholder: "Anything else Martian should know that doesn't fit above", multiline: true,
    guidance: 'Add material facts the website may not explain: business-model nuances, exclusions, constraints, terminology or strategic context.' },
  // P2.1: appended, never inserted -- CompanyContextPage.js and
  // BrandDiscoveryPage.js both reference FIELD_META by numeric index
  // (FIELD_META[0]..[4]), so a new entry must only ever be added at the
  // end, never shift those positions.
  { key: 'company_name', label: 'Company / brand name', placeholder: "The brand's own real name (used to ask AI Search about it correctly) -- e.g. Webflow, not webflow.com",
    guidance: 'Check this is the name customers recognise — not just the website domain or a legal parent company.' },
];

export const LIST_FIELD_META = [
  { key: 'markets_served', label: 'Markets served', placeholder: 'Add a market (e.g. Saudi Arabia)',
    guidance: 'List the geographies the business actively serves today. Remove aspirational or unsupported coverage.' },
  { key: 'growth_priorities', label: 'Growth priorities', placeholder: 'Add a growth priority (e.g. Launch on Amazon)',
    guidance: "Add a few concrete priorities for the coming months. Leave this blank if they aren't decided yet." },
];

export const CONTEXT_FIELD_GUIDANCE = Object.freeze(
  Object.fromEntries([...FIELD_META, ...LIST_FIELD_META].map((m) => [m.key, m.guidance])),
);

// Provenance of one Company Context field, from the backend contract
// (company_context.py): a populated value is founder-confirmed only when
// source === "user"; any other populated value was inferred by Martian and
// is not confirmed; an empty value is missing. One definition, so the
// Review prompt, the inferred indicator and every review count agree --
// and an unknown source is never treated as confirmed.
export function hasContextValue(field) {
  const value = field?.value;
  if (Array.isArray(value)) return value.some((v) => typeof v === 'string' && v.trim().length > 0);
  return typeof value === 'string' && value.trim().length > 0;
}

export function contextProvenance(field) {
  if (!hasContextValue(field)) return 'missing';
  return field?.source === 'user' ? 'confirmed' : 'inferred';
}

// The API's own message when it sent a readable one, otherwise a generic one.
export function saveErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  return typeof detail === 'string' && detail.trim() ? detail : "Couldn't save that — please try again";
}

const POPOVER_ALIGN = {
  end: 'right-0',
  start: 'left-0',
  // Right-aligned on narrow screens (the trigger sits at the row's right
  // edge), left-aligned from md up (the trigger ends a narrow label column).
  responsive: 'right-0 md:right-auto md:left-0',
};

function SourceNote({ field }) {
  if (contextProvenance(field) !== 'inferred') return null;
  return (
    <p className="flex items-center gap-1 text-[10.5px] text-[var(--text-tertiary)] mt-1" data-testid="company-context-inferred-note">
      <Info size={11} /> Inferred by Martian — edit to confirm or replace.
    </p>
  );
}

// Field-level review prompt: "Review" for an inferred value (with a
// "Confirm accurate" action that persists through the caller's real save),
// "Add if relevant" for a missing one, nothing for a confirmed one. The
// popover is opened only by the founder, closes on Escape, an outside click
// or Close, and returns focus to its trigger.
export function FieldReviewHint({ meta, field, guidance = meta?.guidance, onConfirm, align = 'end' }) {
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const popoverId = `context-review-popover-${meta.key}`;

  // Bring an opened popover fully into view (no-op when it already is); its
  // scroll margins keep it clear of fixed bars such as the mobile tab bar.
  useEffect(() => {
    if (open) popoverRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      const hadFocus = containerRef.current?.contains(document.activeElement);
      setOpen(false);
      if (hadFocus) triggerRef.current?.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);
  const provenance = contextProvenance(field);
  const inferred = provenance === 'inferred';

  // Confirmed facts stay quiet. Only inferred facts and genuinely missing fields receive prompts.
  if (!guidance || provenance === 'confirmed') return null;

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const confirm = async () => {
    if (!inferred || !onConfirm || confirming) return;
    setConfirming(true);
    try {
      const saved = await onConfirm(meta.key, field.value);
      if (saved !== false) setOpen(false);
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div ref={containerRef} className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-controls={popoverId}
        aria-label={`Review guidance for ${meta.label}`}
        data-testid={`context-review-help-${meta.key}`}
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-1 text-[10.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)] ${inferred ? 'border-[var(--accent-border)] bg-[var(--accent-light)] text-[var(--accent)]' : 'border-[var(--border)] text-[var(--text-secondary)] hover:bg-[var(--canvas-tertiary)]'}`}
      >
        <Info size={12} /> {inferred ? 'Review' : 'Add if relevant'}
      </button>
      {open && (
        <div
          ref={popoverRef}
          id={popoverId}
          role="group"
          aria-label={`Review ${meta.label}`}
          data-testid={`context-review-popover-${meta.key}`}
          className={`absolute ${POPOVER_ALIGN[align] || POPOVER_ALIGN.end} top-full z-30 mt-2 w-72 max-w-[calc(100vw_-_3rem)] scroll-mt-4 scroll-mb-24 rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-4 text-left shadow-[var(--shadow-card)]`}
        >
          <div className="flex items-start justify-between gap-3">
            <p className="text-[12.5px] font-semibold text-[var(--text-primary)]">
              {inferred ? 'Please check this detail' : 'Not set yet'}
            </p>
            <button type="button" onClick={close} aria-label="Close review guidance" className="rounded p-0.5 text-[var(--text-tertiary)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]">
              <X size={14} />
            </button>
          </div>
          <p className="mt-2 text-[12.5px] leading-relaxed text-[var(--text-secondary)]">{guidance}</p>
          <p className="mt-2 text-[11.5px] leading-relaxed text-[var(--text-tertiary)]">
            {inferred
              ? "Martian inferred this; you haven't confirmed it yet. Confirm it or correct it before relying on it."
              : 'Leave it blank if it is unknown or not relevant. Martian will not treat a missing detail as a fact.'}
          </p>
          <div className="mt-3 flex items-center justify-end gap-3">
            <button type="button" onClick={close} className="rounded-sm text-[11.5px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]">
              Close
            </button>
            {inferred && (
              <button
                type="button"
                onClick={confirm}
                disabled={confirming}
                aria-busy={confirming || undefined}
                data-testid={`context-review-confirm-${meta.key}`}
                className="inline-flex items-center gap-1 rounded-[var(--radius-button)] bg-[var(--accent)] px-2.5 py-1.5 text-[11.5px] font-semibold text-white disabled:opacity-60 focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]"
              >
                <Check size={12} /> {confirming ? 'Confirming…' : 'Confirm accurate'}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function EditableTextField({ meta, field, onSave, reviewHint, onReviewConfirm }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(field?.value || '');
  const [saving, setSaving] = useState(false);

  const startEdit = () => { setDraft(field?.value || ''); setEditing(true); };
  const cancel = () => setEditing(false);
  const save = async () => {
    setSaving(true);
    try {
      const saved = await onSave(meta.key, draft.trim());
      if (saved !== false) setEditing(false);
    } finally {
      setSaving(false);
    }
  };
  const clear = async () => {
    setSaving(true);
    try { const saved = await onSave(meta.key, ''); if (saved !== false) setEditing(false); } finally { setSaving(false); }
  };

  return (
    <div className={`py-4 border-b border-[var(--border-light)] last:border-b-0 ${contextProvenance(field) === 'inferred' ? 'border-l-2 border-l-[var(--accent)] pl-3' : ''}`} data-testid={`context-field-${meta.key}`}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)]">{meta.label}</p>
        <div className="flex items-center gap-2">
          {reviewHint && <FieldReviewHint meta={meta} field={field} guidance={reviewHint} onConfirm={onReviewConfirm} />}
          {!editing && (
            <button type="button" onClick={startEdit} data-testid={`context-edit-${meta.key}`} className="rounded-sm text-[var(--text-tertiary)] hover:text-[var(--accent)] shrink-0 focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]" aria-label={`Edit ${meta.label}`}>
              <PencilSimple size={14} />
            </button>
          )}
        </div>
      </div>
      {!editing ? (
        <>
          {field?.value ? (
            <p className="text-[13.5px] text-[var(--text-primary)] leading-relaxed mt-1 whitespace-pre-wrap" data-testid={`context-value-${meta.key}`}>{field.value}</p>
          ) : (
            <p className="text-[13px] text-[var(--text-tertiary)] italic mt-1" data-testid={`context-empty-${meta.key}`}>Not set yet — Martian hasn't invented anything here.</p>
          )}
          <SourceNote field={field} />
        </>
      ) : (
        <div className="mt-2 space-y-2">
          {meta.multiline ? (
            <textarea
              autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={meta.placeholder} rows={3}
              data-testid={`context-input-${meta.key}`}
              className="w-full text-[13px] rounded-[var(--radius-card-soft)] border border-[var(--border-light)] bg-[var(--surface-inset)] shadow-[var(--shadow-inset)] text-[var(--text-primary)] p-2.5 resize-y focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]"
            />
          ) : (
            <input
              autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={meta.placeholder}
              data-testid={`context-input-${meta.key}`}
              className="w-full text-[13px] rounded-[var(--radius-card-soft)] border border-[var(--border-light)] bg-[var(--surface-inset)] shadow-[var(--shadow-inset)] text-[var(--text-primary)] p-2.5 focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]"
            />
          )}
          <div className="flex items-center gap-2">
            <button type="button" onClick={save} disabled={saving} data-testid={`context-save-${meta.key}`} className="flex items-center gap-1 text-[12px] font-semibold text-white bg-[var(--text-primary)] hover:bg-[var(--text-secondary)] px-3 py-1.5 rounded-[var(--radius-button)] disabled:opacity-60">
              <Check size={12} /> {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={cancel} disabled={saving} className="flex items-center gap-1 text-[12px] font-medium text-[var(--text-secondary)] px-2 py-1.5">
              <X size={12} /> Cancel
            </button>
            {field?.value && (
              <button type="button" onClick={clear} disabled={saving} data-testid={`context-clear-${meta.key}`} className="flex items-center gap-1 text-[12px] font-medium text-[var(--text-tertiary)] hover:text-[var(--error)] ml-auto">
                <Trash size={12} /> Remove
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function EditableListField({ meta, field, onSave, reviewHint, onReviewConfirm }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const values = field?.value || [];

  const addItem = async () => {
    const v = draft.trim();
    if (!v) { setAdding(false); return; }
    // Already listed (case-insensitive): nothing to save.
    if (values.some((existing) => existing.toLowerCase() === v.toLowerCase())) { setDraft(''); setAdding(false); return; }
    setSaving(true);
    try { const saved = await onSave(meta.key, [...values, v]); if (saved !== false) { setDraft(''); setAdding(false); } } finally { setSaving(false); }
  };
  const removeItem = async (item) => {
    setSaving(true);
    try { await onSave(meta.key, values.filter((v) => v !== item)); } finally { setSaving(false); }
  };

  return (
    <div className={`py-4 border-b border-[var(--border-light)] last:border-b-0 ${contextProvenance(field) === 'inferred' ? 'border-l-2 border-l-[var(--accent)] pl-3' : ''}`} data-testid={`context-field-${meta.key}`}>
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)]">{meta.label}</p>
        <div className="flex items-center gap-2">
          {reviewHint && <FieldReviewHint meta={meta} field={field} guidance={reviewHint} onConfirm={onReviewConfirm} />}
          {!adding && (
            <button type="button" onClick={() => setAdding(true)} data-testid={`context-add-${meta.key}`} className="flex items-center gap-1 rounded-sm text-[11.5px] font-medium text-[var(--accent)] focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]">
              <Plus size={12} /> Add
            </button>
          )}
        </div>
      </div>
      {values.length === 0 && !adding && (
        <p className="text-[13px] text-[var(--text-tertiary)] italic" data-testid={`context-empty-${meta.key}`}>Not set yet — Martian hasn't invented anything here.</p>
      )}
      <SourceNote field={field} />
      {values.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {values.map((v, i) => (
            <span key={`${i}-${v}`} data-testid={`context-tag-${meta.key}`} className="flex items-center gap-1 text-[12px] text-[var(--text-primary)] bg-[var(--canvas-secondary)] pl-2.5 pr-1.5 py-1 rounded-[var(--radius-badge)]">
              {v}
              <button type="button" onClick={() => removeItem(v)} disabled={saving} aria-label={`Remove ${v}`} className="rounded-sm text-[var(--text-tertiary)] hover:text-[var(--error)] focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]"><X size={11} /></button>
            </span>
          ))}
        </div>
      )}
      {adding && (
        <div className="flex items-center gap-2 mt-1">
          <input
            autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={meta.placeholder}
            onKeyDown={(e) => { if (e.key === 'Enter') addItem(); if (e.key === 'Escape') setAdding(false); }}
            data-testid={`context-input-${meta.key}`}
            className="flex-1 text-[13px] rounded-[var(--radius-card-soft)] border border-[var(--border-light)] bg-[var(--surface-inset)] shadow-[var(--shadow-inset)] text-[var(--text-primary)] px-2.5 py-1.5 focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]"
          />
          <button type="button" onClick={addItem} disabled={saving} data-testid={`context-save-${meta.key}`} className="text-[12px] font-semibold text-white bg-[var(--text-primary)] px-3 py-1.5 rounded-[var(--radius-button)] disabled:opacity-60">{saving ? 'Adding…' : 'Add'}</button>
          <button type="button" onClick={() => setAdding(false)} disabled={saving} className="text-[12px] text-[var(--text-secondary)] px-1">Cancel</button>
        </div>
      )}
    </div>
  );
}

function CompetitorsSection({ clientId }) {
  const [competitors, setCompetitors] = useState(null); // null = loading
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [saving, setSaving] = useState(false);
  // Phase 0 (P0.4): competitors Martian found in this workspace's own AI
  // answers. Suggestions only -- nothing is added until you approve it.
  const [suggestions, setSuggestions] = useState([]);
  const loadSeq = useRef(0); // only the latest load (this client) may set state

  const load = () => {
    if (!clientId) return;
    setCompetitors(null); // never show the previously-selected client's competitors while switching
    setSuggestions([]);
    const seq = ++loadSeq.current;
    const current = () => seq === loadSeq.current;
    competitorsAPI.list(clientId).then((res) => current() && setCompetitors(res.data || [])).catch(() => current() && setCompetitors([]));
    competitorSuggestionsAPI.list(clientId, { refresh: true })
      .then((res) => current() && setSuggestions((res.data?.suggestions || []).filter((s) => s.status === 'suggested')))
      .catch(() => current() && setSuggestions([]));
  };
  useEffect(load, [clientId]);

  const decideSuggestion = async (suggestion, approve) => {
    setSaving(true);
    try {
      if (approve) await competitorSuggestionsAPI.approve(clientId, suggestion.id);
      else await competitorSuggestionsAPI.reject(clientId, suggestion.id);
      load();
    } catch {
      toast.error("Couldn't save that — please try again");
    } finally {
      setSaving(false);
    }
  };

  const addCompetitor = async () => {
    if (!name.trim()) { setAdding(false); return; }
    setSaving(true);
    try {
      await competitorsAPI.create(clientId, { brand_name: name.trim(), domain: domain.trim(), competitor_type: 'primary' });
      setName(''); setDomain(''); setAdding(false);
      load();
    } catch {
      toast.error("Couldn't add that competitor — please try again");
    } finally {
      setSaving(false);
    }
  };
  const removeCompetitor = async (id) => {
    setSaving(true);
    try { await competitorsAPI.remove(id); load(); }
    catch { toast.error("Couldn't remove that competitor — please try again"); }
    finally { setSaving(false); }
  };

  return (
    <div className="py-4 border-b border-[var(--border-light)]" data-testid="context-field-competitors">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div>
          <p className="text-[11px] font-mono uppercase tracking-wide text-[var(--text-tertiary)]">Competitors</p>
          <p className="text-[10.5px] text-[var(--text-tertiary)] mt-0.5">Add them yourself, or approve the ones Martian suggests. Nothing is added without your approval.</p>
        </div>
        {!adding && (
          <button onClick={() => setAdding(true)} data-testid="context-add-competitor" className="flex items-center gap-1 text-[11.5px] font-medium text-[var(--accent)] shrink-0">
            <Plus size={12} /> Add
          </button>
        )}
      </div>
      {competitors === null && <p className="text-[12px] text-[var(--text-tertiary)]">Loading…</p>}
      {competitors?.length === 0 && !adding && (
        <p className="text-[13px] text-[var(--text-tertiary)] italic" data-testid="context-empty-competitors">No competitors added yet.</p>
      )}
      {competitors?.length > 0 && (
        <ul className="space-y-1.5 mb-2">
          {competitors.map((c) => (
            <li key={c.id} data-testid="context-competitor-row" className="flex items-center justify-between text-[13px] bg-[var(--canvas-secondary)] rounded-[var(--radius-badge)] px-3 py-1.5">
              <span className="text-[var(--text-primary)] font-medium">{c.brand_name}{c.domain ? <span className="text-[var(--text-tertiary)] font-normal"> — {c.domain}</span> : null}</span>
              <button onClick={() => removeCompetitor(c.id)} disabled={saving} className="text-[var(--text-tertiary)] hover:text-[var(--error)]"><Trash size={13} /></button>
            </li>
          ))}
        </ul>
      )}
      {suggestions.length > 0 && (
        <div className="mt-2 mb-2" data-testid="context-competitor-suggestions">
          <p className="text-[10.5px] font-mono uppercase tracking-wide text-[var(--text-tertiary)] mb-1.5">Suggested by Martian</p>
          <ul className="space-y-1.5">
            {suggestions.map((s) => (
              <li key={s.id} data-testid="context-competitor-suggestion" className="flex items-center justify-between gap-3 text-[13px] border border-dashed border-[var(--border-light)] rounded-[var(--radius-badge)] px-3 py-1.5">
                <span className="min-w-0">
                  <span className="text-[var(--text-primary)] font-medium">{s.name}</span>
                  {s.reason && <span className="block text-[11px] text-[var(--text-tertiary)]">{s.reason}</span>}
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <button onClick={() => decideSuggestion(s, true)} disabled={saving} data-testid="context-competitor-suggestion-approve" className="text-[11.5px] font-semibold text-[var(--accent)] disabled:opacity-60">Approve</button>
                  <button onClick={() => decideSuggestion(s, false)} disabled={saving} data-testid="context-competitor-suggestion-reject" className="text-[11.5px] text-[var(--text-secondary)] disabled:opacity-60">Not a competitor</button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {adding && (
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Competitor name" data-testid="context-competitor-name-input"
            className="flex-1 min-w-[140px] text-[13px] rounded-[var(--radius-card-soft)] border border-[var(--border-light)] bg-[var(--surface-inset)] shadow-[var(--shadow-inset)] text-[var(--text-primary)] px-2.5 py-1.5 focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]" />
          <input value={domain} onChange={(e) => setDomain(e.target.value)} placeholder="Domain (optional)" data-testid="context-competitor-domain-input"
            className="flex-1 min-w-[140px] text-[13px] rounded-[var(--radius-card-soft)] border border-[var(--border-light)] bg-[var(--surface-inset)] shadow-[var(--shadow-inset)] text-[var(--text-primary)] px-2.5 py-1.5 focus-visible:outline-none focus-visible:[box-shadow:var(--shadow-focus-ring)]" />
          <button onClick={addCompetitor} disabled={saving} data-testid="context-competitor-save" className="text-[12px] font-semibold text-white bg-[var(--text-primary)] px-3 py-1.5 rounded-[var(--radius-button)] disabled:opacity-60">Add</button>
          <button onClick={() => setAdding(false)} className="text-[12px] text-[var(--text-secondary)] px-1">Cancel</button>
        </div>
      )}
    </div>
  );
}

export default function CompanyContextPage() {
  // P1.8: client resolution is now the shared useWorkspaceSelection() hook
  // (see SignalsPage.js's own comment for the full rationale) instead of
  // an unswitchable first-client fetch -- this page had the identical
  // defect the P1.7 audit fixed on Signals/Recommendations/Action Plans,
  // just missed in that pass.
  const { clients, workspaceId, setWorkspaceId, loadingClients } = useWorkspaceSelection();
  const clientId = workspaceId || null;
  const [context, setContext] = useState(null); // null = loading
  const [loadError, setLoadError] = useState(false);
  const activeClientId = useRef(clientId);
  activeClientId.current = clientId;

  useEffect(() => {
    if (!loadingClients && clients.length === 0) setLoadError('no-client');
  }, [loadingClients, clients]);

  useEffect(() => {
    if (!clientId) return undefined;
    let cancelled = false;
    setContext(null); // never show the previously-selected client's fields while switching
    companyContextAPI.get(clientId).then((res) => { if (!cancelled) setContext(res.data); }).catch(() => { if (!cancelled) setLoadError('error'); });
    return () => { cancelled = true; };
  }, [clientId]);

  // Returns false on failure so the editor stays open with the founder's
  // draft; a response for a workspace no longer selected is not shown.
  const saveField = async (key, value) => {
    const requestedFor = clientId;
    try {
      const res = await companyContextAPI.update(requestedFor, { [key]: value });
      if (activeClientId.current === requestedFor) setContext(res.data);
      return true;
    } catch (err) {
      toast.error(saveErrorMessage(err));
      return false;
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto" data-testid="company-context-page">
        <div className="mb-5">
          <h1 className="font-display text-[20px] font-bold text-[var(--text-primary)] mb-1">Company Context</h1>
          <p className="text-[12.5px] text-[var(--text-secondary)]">
            What Martian understands about your business. You're always in control — add, correct, or remove anything below.
          </p>
        </div>

        {!loadError && clients.length > 0 && (
          <div className="mb-4">
            <WorkspaceSelect clients={clients} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} />
          </div>
        )}

        {loadError === 'no-client' && (
          <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-8 text-center text-[13px] text-[var(--text-secondary)]">
            Add your brand first to build its Company Context.
          </div>
        )}
        {loadError === 'error' && (
          <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-8 text-center text-[13px] text-[var(--text-secondary)]">
            We couldn't load Company Context right now — refresh to try again.
          </div>
        )}

        {!loadError && (context === null || clientId === null) && (
          <div className="rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] p-8 text-center text-[13px] text-[var(--text-tertiary)]">
            Loading…
          </div>
        )}

        {/* Day 2.5 -- the one raised/focal surface on this page: the whole
             editable form is the primary content, so it gets the softer,
             larger-radius "lifted" treatment rather than the flat default. */}
        {!loadError && context && clientId && (
          <div className="rounded-[var(--radius-card-soft)] border border-[var(--border-light)] bg-[var(--surface-raised)] shadow-[var(--shadow-raised)] px-5 sm:px-6" data-testid="company-context-form">
            {/* Order matches the approved spec exactly: Business ->
                 Primary market -> Markets served -> Audience ->
                 Competitors -> Positioning -> Growth priorities ->
                 Additional context. Company/brand name (P2.1) shown first
                 -- identity precedes description -- using FIELD_META[5],
                 appended rather than inserted so it never shifts the
                 existing indices below. */}
            <EditableTextField meta={FIELD_META[5]} field={context.company_name} onSave={saveField} />
            <EditableTextField meta={FIELD_META[0]} field={context.business_summary} onSave={saveField} />
            <EditableTextField meta={FIELD_META[1]} field={context.primary_market} onSave={saveField} />
            <EditableListField meta={LIST_FIELD_META[0]} field={context.markets_served} onSave={saveField} />
            <EditableTextField meta={FIELD_META[2]} field={context.audience} onSave={saveField} />
            <CompetitorsSection clientId={clientId} />
            <EditableTextField meta={FIELD_META[3]} field={context.positioning} onSave={saveField} />
            <EditableListField meta={LIST_FIELD_META[1]} field={context.growth_priorities} onSave={saveField} />
            <EditableTextField meta={FIELD_META[4]} field={context.additional_context} onSave={saveField} />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
